import { log } from "@logtail/next";
import * as Sentry from "@sentry/nextjs";
import { shouldSendToBetterStack } from "./should-send-to-betterstack";

/**
 * Converts class instances to plain objects for logging.
 */
function convertToPlainObject(obj: any): { [key: string]: any } | undefined {
  if (obj === undefined || obj === null) {
    return undefined;
  }

  // Early return for primitive types
  if (typeof obj !== "object") {
    return { data: obj };
  }

  // Early return for plain objects
  const proto = Object.getPrototypeOf(obj);
  if (proto === Object.prototype || proto === null) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(convertToPlainObject);
  }

  // Convert class instances to plain objects
  const plainObj: Record<string, any> = {};
  for (const key of Object.getOwnPropertyNames(obj)) {
    plainObj[key] = convertToPlainObject(obj[key]);
  }
  return plainObj;
}

export function createLogger(entityName: string) {
  return new Logger(entityName);
}

/** Upper bound on how long a flush may hold its caller open. */
const FLUSH_TIMEOUT_MS = 2000;

/** Resolves after `ms`, without keeping the event loop alive. */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    timer.unref?.();
  });
}

type LogLevel = "debug" | "info" | "warn" | "error";
type LogPayload = ReturnType<typeof convertToPlainObject>;

export interface LogOptions {
  /**
   * Set `false` for a payload carrying PHI or LLM tool arguments: the line still
   * reaches BetterStack and the console, but is not mirrored into `Sentry.logger`.
   * Every payload is mirrored by default, so choosing `logger` over
   * `Sentry.captureException` is not what keeps a field out of Sentry.
   */
  mirrorToSentry: boolean;
}

interface SentryMirrorExtras {
  payload?: LogPayload;
  options?: LogOptions;
}

export class Logger {
  constructor(
    private entityName: string,
    private defaults: Record<string, unknown> = {},
  ) {}

  /**
   * Returns a child logger that merges the given fields into every log
   * payload it emits. Useful for adding correlation IDs (e.g. runId,
   * inngestEventId) once at the call site instead of per log line.
   */
  with(defaults: Record<string, unknown>): Logger {
    return new Logger(this.entityName, { ...this.defaults, ...defaults });
  }

  /**
   * Prefixes messages with the entity name for easier traceability.
   */
  private formatMessage(message: string) {
    return this.entityName ? `${this.entityName}: ${message}` : message;
  }

  /**
   * Converts any provided args into a serializable payload while preserving falsy values.
   * Merges any bound defaults from `.with()` so child loggers inject correlation fields
   * into every log line.
   */
  private formatArgs(args?: unknown): LogPayload {
    const hasDefaults = Object.keys(this.defaults).length > 0;

    if (args === null || args === undefined) {
      return hasDefaults ? { ...this.defaults } : undefined;
    }

    const converted = convertToPlainObject(args);
    if (!hasDefaults) {
      return converted;
    }

    if (converted && typeof converted === "object" && !Array.isArray(converted)) {
      return { ...this.defaults, ...converted };
    }

    return { ...this.defaults, data: converted };
  }

  /**
   * Console is the sink of last resort. A local `next start` run sets NODE_ENV to production
   * while BetterStack and Sentry are both off, so without this branch its logs reach nothing.
   */
  private shouldMirrorToConsole() {
    return process.env.NODE_ENV !== "production" || !shouldSendToBetterStack();
  }

  /**
   * Ships a log entry to BetterStack. Local never sends. See `shouldSendToBetterStack`.
   */
  private sendToBetterStack(level: LogLevel, message: string, payload?: LogPayload) {
    if (!shouldSendToBetterStack()) return;
    log[level](message, payload);
  }

  /**
   * Mirrors log entries to Sentry when Sentry's structured logger is available.
   */
  private mirrorToSentry(
    level: LogLevel,
    message: string,
    { payload, options }: SentryMirrorExtras,
  ) {
    if (options?.mirrorToSentry === false) return;
    // Guard against Sentry not being initialized (e.g., in development)
    if (!Sentry.logger) return;

    const attrs = payload ? { ...payload, entity: this.entityName } : { entity: this.entityName };

    try {
      switch (level) {
        case "debug":
          Sentry.logger.debug?.(message, attrs);
          break;
        case "info":
          Sentry.logger.info?.(message, attrs);
          break;
        case "warn":
          Sentry.logger.warn?.(message, attrs);
          break;
        case "error":
          Sentry.logger.error?.(message, attrs);
          break;
      }
    } catch {
      // Fail silently - logging should never crash the app
      return;
    }
  }

  /**
   * Mirrors log entries to the console in non-production environments to aid local debugging.
   *
   * Payload is logged via a separate `console.log` call rather than as a second arg to
   * `console.error`/`console.warn`. Sentry's `captureConsoleIntegration` hooks error/warn
   * and joins args with `String(arg)` — passing an object as a second arg turns the event
   * title into "message [object Object]". Splitting the payload onto its own `console.log`
   * keeps the devtools-inspectable object while preventing the bogus Sentry events.
   */
  private mirrorToConsole(level: LogLevel, message: string, payload?: LogPayload) {
    if (!this.shouldMirrorToConsole()) {
      return;
    }

    switch (level) {
      case "debug":
        console.debug(message);
        break;
      case "info":
        console.info(message);
        break;
      case "warn":
        console.warn(message);
        break;
      case "error":
        console.error(message);
        break;
      default:
        console.log(message);
    }
    if (payload !== undefined) {
      console.log(payload);
    }
  }

  debug(message: string, args?: any, options?: LogOptions) {
    const formattedMessage = this.formatMessage(message);
    const payload = this.formatArgs(args);
    this.sendToBetterStack("debug", formattedMessage, payload);
    this.mirrorToConsole("debug", formattedMessage, payload);
    this.mirrorToSentry("debug", formattedMessage, { payload, options });
  }

  info(message: string, args?: any, options?: LogOptions) {
    const formattedMessage = this.formatMessage(message);
    const payload = this.formatArgs(args);
    this.sendToBetterStack("info", formattedMessage, payload);
    this.mirrorToConsole("info", formattedMessage, payload);
    this.mirrorToSentry("info", formattedMessage, { payload, options });
  }

  warn(message: string, args?: any, options?: LogOptions) {
    const formattedMessage = this.formatMessage(message);
    const payload = this.formatArgs(args);
    this.sendToBetterStack("warn", formattedMessage, payload);
    this.mirrorToConsole("warn", formattedMessage, payload);
    this.mirrorToSentry("warn", formattedMessage, { payload, options });
  }

  /**
   * When converting logs into metrics on log fields, it's best to have low
   * cardinality of that field. The message field is an example of something that would have
   * too high cardinality to turn into a metric. Instead, we'll keep track of a field called "inconsistencyVendor".
   * We can turn log lines with this field into metrics, allowing us to graph and alert on it.
   *
   * In this function, we assume medplum is involved as the other data source.
   * We are not consistent in which system we view as the source of truth.
   */
  errorDataInconsistency(message: string, vendorName: string, args?: any) {
    const logArgs = {
      ...(args ?? {}),
      errorType: "dataInconsistency",
      vendor: vendorName,
    };
    this.error(`Data Inconsistency Error: ${message}`, logArgs);
  }

  error(message: string, args?: any, options?: LogOptions) {
    const formattedMessage = this.formatMessage(message);
    const payload = this.formatArgs(args);
    this.sendToBetterStack("error", formattedMessage, payload);
    this.mirrorToConsole("error", formattedMessage, payload);
    this.mirrorToSentry("error", formattedMessage, { payload, options });
  }

  /**
   * Delivers queued BetterStack logs before short-lived server work returns.
   *
   * Bounded: `@logtail/next` POSTs with no timeout or AbortSignal, so an
   * unreachable ingest host would otherwise hold the caller open until the
   * platform kills it. Losing a log line beats stalling the work that emitted it.
   */
  async flush(): Promise<void> {
    if (!shouldSendToBetterStack()) return;
    try {
      await Promise.race([log.flush(), delay(FLUSH_TIMEOUT_MS)]);
    } catch {
      // logging delivery must never replace the original application outcome
    }
  }
}
